import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface Toast {
  id: string;
  tone: ToastTone;
  message: string;
  /** Milliseconds before it dismisses itself. `0` keeps it until dismissed. */
  duration: number;
}

export interface ToastOptions {
  tone?: ToastTone;
  duration?: number;
}

interface ToastValue {
  toasts: readonly Toast[];
  show: (message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

/** Long enough to read a sentence; the WCAG 2.2.1 minimum is 20 seconds. */
const DEFAULT_DURATION = 6000;

export interface ToastProviderProps {
  children: ReactNode;
  /**
   * Accessible name for every toast's dismiss control.
   *
   * Passed in rather than translated here so this primitive keeps knowing
   * nothing about the message catalogue — the same rule every other component
   * in this directory follows.
   */
  dismissLabel: string;
}

/**
 * Transient confirmations — saved, deleted, restored.
 *
 * Built here rather than pulled in, because the only part that is hard is the
 * announcement, and that is about twenty lines:
 *
 *  - **A live region that exists before the message does.** A region inserted
 *    at the same moment as its content is frequently not announced at all;
 *    screen readers watch existing regions for changes. The container is
 *    therefore always rendered, empty.
 *  - **Politeness by tone.** Failures are `assertive` and interrupt; successes
 *    are `polite` and wait for a pause. Announcing "Saved" over someone mid-
 *    sentence is worse than telling them a second later.
 *  - **A dismiss control on every toast.** WCAG 2.2.1 requires that a timed
 *    message can be dismissed, and someone using a screen magnifier may need
 *    far longer than six seconds to find it.
 *
 * A toast must never be the only record of something. It vanishes, it is
 * invisible to anyone who looked away, and it cannot be recovered. Errors that
 * need action belong in an `Alert` on the form.
 */
export function ToastProvider({ children, dismissLabel }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const duration = options.duration ?? DEFAULT_DURATION;

      setToasts((current) => [...current, { id, tone: options.tone ?? 'info', message, duration }]);

      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }

      return id;
    },
    [dismiss],
  );

  // Timers outlive the component otherwise, and fire setState on an unmounted
  // tree during a route change.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo<ToastValue>(() => ({ toasts, show, dismiss }), [toasts, show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport dismissLabel={dismissLabel} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error('useToast was called outside ToastProvider');
  }
  return value;
}

const tones = {
  info: { icon: Info, className: 'bg-info-bg border-info-border text-info-fg' },
  success: { icon: CheckCircle2, className: 'bg-success-bg border-success-border text-success-fg' },
  warning: {
    icon: AlertTriangle,
    className: 'bg-warning-bg border-warning-border text-warning-fg',
  },
  danger: { icon: XCircle, className: 'bg-danger-bg border-danger-border text-danger-fg' },
} as const;

/**
 * Where toasts appear.
 *
 * Two regions, not one, because politeness is a property of the region rather
 * than of the message inside it. A single region would have to pick one, and
 * either failures wait politely or successes interrupt.
 */
function ToastViewport({ dismissLabel }: { dismissLabel: string }) {
  const { toasts, dismiss } = useToast();

  return (
    <div className="pointer-events-none fixed start-0 end-0 bottom-0 z-70 flex flex-col items-center gap-2 p-4 sm:items-end">
      <Region
        politeness="polite"
        toasts={toasts.filter((toast) => toast.tone !== 'danger')}
        dismiss={dismiss}
        dismissLabel={dismissLabel}
      />
      <Region
        politeness="assertive"
        toasts={toasts.filter((toast) => toast.tone === 'danger')}
        dismiss={dismiss}
        dismissLabel={dismissLabel}
      />
    </div>
  );
}

function Region({
  politeness,
  toasts,
  dismiss,
  dismissLabel,
}: {
  politeness: 'polite' | 'assertive';
  toasts: readonly Toast[];
  dismiss: (id: string) => void;
  dismissLabel: string;
}) {
  return (
    // Always present, even with nothing in it: a live region created at the
    // same instant as its content is often missed entirely.
    <div
      aria-live={politeness}
      aria-atomic="false"
      className="flex w-full flex-col gap-2 sm:w-auto"
    >
      {toasts.map((toast) => {
        const { icon: Icon, className } = tones[toast.tone];

        return (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex w-full items-start gap-3 rounded-md border p-3 shadow-md sm:w-80',
              className,
            )}
          >
            <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <p className="flex-1 text-sm">{toast.message}</p>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="rounded-sm p-0.5 opacity-70 transition-opacity hover:opacity-100"
            >
              <X className="size-4" aria-hidden="true" />
              <span className="hs-sr-only">{dismissLabel}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
